"use client";

import Link from "next/link";

import { useUser } from "@/hooks/useUser";
import { useAnalytics } from "@/lib/analytics";

import type { CtaLocation } from "@/lib/analytics";
import type { MouseEvent } from "react";

type AuthLinkProps = Omit<React.ComponentProps<typeof Link>, "to"> & {
	/** Stable identifier for the CTA, e.g. `deploy_first_agent`. */
	ctaId?: string;
	/** Marketing surface this CTA sits on. Required to emit `cta_clicked`. */
	ctaLocation?: CtaLocation;
	/** Visible label, defaults to the link's text content. */
	ctaText?: string;
};

export function AuthLink({
	ctaId,
	ctaLocation,
	ctaText,
	onClick,
	...props
}: AuthLinkProps) {
	const { user, isLoading } = useUser();
	const { capture } = useAnalytics();
	const href = user && !isLoading ? "/dashboard" : "/signup";

	const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
		if (ctaId && ctaLocation) {
			capture("cta_clicked", {
				cta_id: ctaId,
				cta_text: ctaText ?? event.currentTarget.innerText,
				location: ctaLocation,
				destination: href,
			});
		}
		onClick?.(event);
	};

	return <Link {...props} href={href} prefetch={true} onClick={handleClick} />;
}
